import { useTransition } from '@react-spring/web';
import type React from 'react';
import { useCallback, useMemo, useRef, useState, type CSSProperties, type PropsWithChildren } from 'react';
import { useOnClickOutside, useOnKeyDown } from '../../hooks';
import type { BoxProps } from '../Core';
import { ResizerPlacement } from '../Resizer/useResizer';
import { DrawerStyleContext } from './DrawerStyleContext';
import { DrawerColumn, DrawerContentWrapper, DrawerResizeHandle, DrawerWrapper, Wrapper } from './styles';
import { useResizableDrawerWidth } from './useResizableDrawerWidth';

export { DrawerContent, DrawerFooter, DrawerHeader } from './styles';

export type DrawerInputs = {
  initialIsOpen?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  position?: 'absolute' | 'relative' | 'fixed';
  placement?: 'right' | 'left';
  resizeable?: boolean;
  minResizeWidth?: number;
  maxResizeWidth?: number;
  resizeAriaLabel?: string;
  width?: number;
  onIsOpenChange?: (isOpen: boolean) => void;
};

export type DrawerProps = {
  isOpen: boolean;
  position: 'absolute' | 'relative' | 'fixed';
  placement?: 'right' | 'left';
  open: () => void;
  close: () => void;
  drawerRef: React.RefObject<HTMLDivElement>;
  contentRef: React.RefObject<HTMLDivElement>;
  resizeable?: boolean;
  minResizeWidth?: number;
  maxResizeWidth?: number;
  resizeAriaLabel?: string;
  width?: number;
  style?: CSSProperties;
  adjustForGapMargin?: CSSProperties['marginRight'];
  drawerHeaderDesign?: 'tab' | 'flex';
} & BoxProps;

export const useDrawer = ({
  initialIsOpen = false,
  closeOnClickOutside = false,
  closeOnEscape = false,
  position = 'fixed',
  placement = 'right',
  resizeable,
  minResizeWidth,
  maxResizeWidth,
  resizeAriaLabel,
  width,
  onIsOpenChange,
}: DrawerInputs): DrawerProps => {
  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const open = useCallback(() => {
    onIsOpenChange?.(true);
    setIsOpen(true);
  }, [onIsOpenChange]);

  const close = useCallback(() => {
    // Blur immediately so parent protected drawers can close on Escape while this drawer is still animating out.
    if (drawerRef.current?.contains(document.activeElement)) {
      (document.activeElement as HTMLElement)?.blur();
    }
    onIsOpenChange?.(false);
    setIsOpen(false);
  }, [onIsOpenChange, drawerRef]);

  useOnClickOutside(drawerRef, isOpen && closeOnClickOutside ? close : () => {});
  useOnKeyDown({ key: 'Escape' }, isOpen && closeOnEscape ? close : undefined, drawerRef);

  return useMemo(
    () => ({
      isOpen,
      open,
      close,
      contentRef,
      drawerRef,
      position,
      placement,
      resizeable,
      minResizeWidth,
      maxResizeWidth,
      resizeAriaLabel,
      width,
    }),
    [
      isOpen,
      open,
      close,
      contentRef,
      drawerRef,
      position,
      placement,
      resizeable,
      minResizeWidth,
      maxResizeWidth,
      resizeAriaLabel,
      width,
    ]
  );
};

export function Drawer({
  open,
  isOpen,
  children,
  position,
  drawerRef,
  contentRef,
  width,
  style,
  placement = 'right',
  adjustForGapMargin,
  drawerHeaderDesign,
  resizeable,
  minResizeWidth,
  maxResizeWidth,
  resizeAriaLabel,
  ...props
}: PropsWithChildren<DrawerProps>) {
  const resizePlacement = placement === 'left' ? ResizerPlacement.RIGHT : ResizerPlacement.LEFT;
  const configuredWidth = width ?? 0;
  const {
    width: resizableWidth,
    isResizing,
    onResizeMouseDown,
    onResizeKeyDown,
  } = useResizableDrawerWidth({
    initialWidth: configuredWidth,
    minWidth: minResizeWidth ?? configuredWidth,
    maxWidth: maxResizeWidth ?? configuredWidth,
    placement: resizePlacement,
    enabled: Boolean(resizeable) && isOpen,
  });
  const drawerWidth = resizeable ? resizableWidth : width;

  const transitions = useTransition(isOpen, {
    from: { transform: placement === 'left' ? 'translateX(-100%)' : 'translateX(100%)' },
    enter: { transform: 'translateX(0%)' },
    leave: { transform: placement === 'left' ? 'translateX(-100%)' : 'translateX(100%)' },
    reverse: !isOpen,
    config: { duration: 200 },
  });

  const marginRight = isOpen ? undefined : adjustForGapMargin != null ? adjustForGapMargin : '-spacing2';
  const openWidth = isOpen ? drawerWidth : 0;

  const drawerStyleContextValue = useMemo(() => ({ drawerHeaderDesign }), [drawerHeaderDesign]);
  if (position === 'relative') {
    const drawerContent = (
      <DrawerWrapper
        {...props}
        ref={drawerRef}
        width={openWidth}
        mr={marginRight}
        style={style}
        $disableTransition={isResizing}
        aria-hidden={isOpen ? undefined : true}
        {...(!isOpen ? { inert: '' } : undefined)}
      >
        <DrawerContentWrapper width={drawerWidth} ref={contentRef} role="dialog">
          <DrawerStyleContext.Provider value={drawerStyleContextValue}>{children}</DrawerStyleContext.Provider>
        </DrawerContentWrapper>
      </DrawerWrapper>
    );

    if (resizeable) {
      return (
        <DrawerColumn $placement={placement} $width={openWidth} $disableTransition={isResizing}>
          {drawerContent}
          {isOpen && (
            <DrawerResizeHandle
              type="button"
              role="separator"
              $placement={placement}
              aria-label={resizeAriaLabel ?? 'Resize drawer'}
              aria-orientation="vertical"
              aria-valuemin={minResizeWidth ?? configuredWidth}
              aria-valuemax={maxResizeWidth ?? configuredWidth}
              aria-valuenow={resizableWidth}
              $isActive={isResizing}
              onMouseDown={onResizeMouseDown}
              onKeyDown={onResizeKeyDown}
            />
          )}
        </DrawerColumn>
      );
    }

    return drawerContent;
  }

  return transitions(
    (styles, item) =>
      item && (
        <Wrapper
          position={position}
          ref={drawerRef}
          style={styles}
          width={drawerWidth}
          placement={placement}
          role="dialog"
        >
          <DrawerStyleContext.Provider value={drawerStyleContextValue}>{children}</DrawerStyleContext.Provider>
        </Wrapper>
      )
  );
}
